/* eslint-disable max-lines */
import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { List, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, ThemeProvider } from "@mui/material";
import { invalidateClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/invalidateClipboardScoreProfile";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useEnableGeofenceTracking } from "@src/appV2/Location/geofence/hooks/useEnableGeofenceTracking";
import { GeofenceProvider, useGeofenceContext } from "@src/appV2/Location/geofence/useGeofence";
import { useDelayedReviewStorage } from "@src/appV2/PostShiftReview/DelayedReview/useDelayedReviewStorage";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { calculateMinutesElapsed } from "@src/appV2/redesign/ArrivalTime/calculateMinutesElapsed";
import { ShiftBottomSheetSpecification } from "@src/appV2/redesign/Shift/BottomSheet/Specification";
import {
  DEFAULT_MESH_AGENCY_ID,
  DEFAULT_MESH_AGENCY_NAME,
  DEFAULT_MESH_TIME_TO_ALLOW_SKIP_IN_SEC,
} from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { type UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import {
  type NfcTagRequestStatus,
  type ShiftPeriod,
  shiftPeriodToTimeSlot,
} from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInMinutes, parseISO } from "date-fns";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";

import { useArrivalTimeConfig } from "../../ArrivalTime/useArrivalTimeConfig";
import { FileUploaderBottomSheet } from "../../components/FileUploaderBottomSheet";
import { PageWrapper } from "../../components/PageWrapper";
import { ModalRoute } from "../../Navigation/ModalRoute";
import { ModalTransitionSwitch } from "../../Navigation/ModalTransitionSwitch";
import { OpenGeoLocationProvider } from "../../OpenGeoLocation/useOpenGeoLocation";
import { ShiftDurationListItem } from "../../Shift/DurationListItem";
import { ShiftHighlightsAndMapListItem } from "../../Shift/HighlightsAndMapListItem";
import { convertShiftToSlotId, useGetShiftSlotsV2 } from "../../Shift/Open/useGetShiftsSlotV2";
import { UPDATE_ETA_BOTTOM_SHEET_SOURCE } from "../../Shift/WorkerEta/UpdateEta/types";
import { ShiftWorkplaceListItem } from "../../Shift/WorkplaceListItem";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "../../ShiftDiscovery/paths";
import { useIsBadgeVerificationRedesignEnabled } from "../../WorkBadges/hooks/useIsBadgeVerificationRedesignEnabled";
import { WorkingThisShiftListItem } from "../../WorkWithFriends/components/WorkingThisShiftListItem";
import { useGetFriendsList } from "../../WorkWithFriends/hooks/useGetFriendsList";
import { WorkWithFriendsProvider } from "../../WorkWithFriends/WorkWithFriendsContext";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../api/useMarkShiftNonInstant";
import { PageContent } from "../components/PageContent";
import { ReturnForSignatureBottomSheet } from "../components/ReturnForSignatureBottomSheet";
import { getSentHomeRequestCreatePath } from "../CreateSentHomeRequest/path";
import {
  MY_SHIFTS_FULL_PATH,
  SHIFT_DETAILS_BADGE_VERIFICATION_STEP_QUERY_PARAM,
  SHIFT_DETAILS_CLOCK_IN_QUERY_PARAM,
  SHIFT_DETAILS_CONFIRM_ATTENDANCE_PATH,
  SHIFT_DETAILS_UPDATE_ETA_PATH,
  SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH,
} from "../path";
import { useAttendanceVerificationMethod } from "../Timekeeping/AttendanceVerificationMethods/useAttendanceVerificationMethods";
import { ClockInBottomSheet } from "../Timekeeping/TimekeepingActions/ClockInBottomSheet";
import { ClockInTimeclockComplianceBottomSheet } from "../Timekeeping/TimekeepingActions/ClockInTimeclockComplianceBottomSheet";
import { ClockInTimekeepingIntegrationBottomSheet } from "../Timekeeping/TimekeepingActions/ClockInTimekeepingIntegrationBottomSheet";
import { ClockInWithVideoValidationBottomSheet } from "../Timekeeping/TimekeepingActions/ClockInWithVideoValidationBottomSheet";
import { type TimekeepingAction } from "../Timekeeping/TimekeepingActions/constants";
import { useTimekeepingActionTimer } from "../Timekeeping/TimekeepingActions/hooks/useTimekeepingActionTimer";
import { TransferShiftBottomSheet } from "../TransferShifts/Transferer/TransferShiftBottomSheet";
import { useGetShiftDetailsData } from "./api/useGetShiftDetailsData";
import { AcceptNonInstantPayBottomSheet } from "./components/AcceptNonInstantPayBottomSheet";
import { BadgeVerificationDialog } from "./components/BadgeVerification/BadgeVerificationDialog";
import { BadgeVerificationDialogRedesign } from "./components/BadgeVerification/BadgeVerificationDialogRedesign";
import { useIsBadgeVerificationRequired } from "./components/BadgeVerification/hooks/useIsBadgeVerificationRequired";
import { BadgeVerificationStep } from "./components/BadgeVerification/types";
import { CancelShiftBottomSheet } from "./components/CancelShiftBottomSheet";
import { ConfirmAttendanceBottomSheet } from "./components/ConfirmAttendanceBottomSheet";
import { ShiftDetailsFooter } from "./components/Footer";
import { ShiftDetailsHeaderWrapper } from "./components/HeaderWrapper";
import { HorizontalCardWrapperListItem } from "./components/HorizontalCardWrapperListItem";
import { ShiftDetailsPageSkeleton } from "./components/Skeleton";
import { TimesheetUploadBottomSheet } from "./components/TimesheetUploadBottomSheet";
import { TooEarlyToClockInBottomSheet } from "./components/TooEarlyToClockInBottomSheet";
import { TooFarAwayBottomSheet } from "./components/TooFarAwayBottomSheet";
import { SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY } from "./constants";
import { useCancelShiftFlow } from "./hooks/useCancelShiftFlow";
import { useTimesheetFileUpload } from "./hooks/useTimesheetFileUpload";
import { ShiftDetailsModalRoutes } from "./ShiftDetailsModalRoutes";
import { useOnClockIn } from "./Timekeeping/hooks/useOnClockIn";
import { ClockInTouchpointBottomSheet } from "./Timekeeping/TouchPoint/TouchpointActions/ClockInTouchpointBottomSheet";

const MIN_GEOFENCE_CHECK_LOADER_DURATION_MS = 1000;

function ShiftDetailsPageContent() {
  const currentDate = useCurrentDate(1000);
  const history = useHistory();
  const location = useLocation();
  const { shiftId } = useParams<{ shiftId: string }>();

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_PAGE_VIEWED,
    { shiftId },
    { enabled: isDefined(shiftId) }
  );

  const clockInModalState = useModalState();
  const tooFarAwayModalState = useModalState();
  const tooEarlyModalState = useModalState();
  const acceptNonInstantPayModalState = useModalState();
  const updateTransferShiftModalState = useModalState();
  const uploadTimesheetModalState = useModalState();
  const uploadingTimesheetModalState = useModalState();
  const postShiftReviewModalState = useModalState();
  const returnForSignatureModalState = useModalState();
  const badgeVerificationModalState = useModalState();
  const [badgeVerificationInitialStep, setBadgeVerificationInitialStep] =
    useState<BadgeVerificationStep>();

  const [isCheckingGeofence, setIsCheckingGeofence] = useState(false);
  const geofenceCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (geofenceCheckTimerRef.current) {
        clearTimeout(geofenceCheckTimerRef.current);
      }
    };
  }, []);

  const {
    modalState: shiftOptionsModalState,
    handleOpenShiftOptions,
    handleCloseShiftOptions,
  } = useShiftOptionsModal();
  const {
    userId,
    firstName,
    lastName,
    isTestAccount: isWorkerTestAccount,
    stage: workerStage,
    substage: workerSubstage,
  } = useDefinedWorker();
  const { showErrorToast } = useToast();
  const { mutateAsync: markNonInstant, isLoading: isConvertingToNonInstantPay } =
    useMarkShiftNonInstant();

  // Timesheet upload callbacks
  const onTimesheetUploadStart = useCallback(() => {
    uploadTimesheetModalState.closeModal();
    uploadingTimesheetModalState.openModal();
  }, [uploadTimesheetModalState, uploadingTimesheetModalState]);

  const onTimesheetUploadError = useCallback(
    (error: Error | CameraPermissionDeniedError) => {
      if (error instanceof CameraPermissionDeniedError) {
        showErrorToast(
          "Camera permission is required to take photos. Please enable camera access in your device settings."
        );
      } else {
        showErrorToast("Something went wrong while uploading your timesheet. Please try again.");
      }
    },
    [showErrorToast]
  );

  const {
    shift,
    shiftState,
    shiftError: shiftsDetailsError,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isLoading: isShiftDetailsLoading,
    refetchShiftState,
  } = useGetShiftDetailsData({ shiftId });

  const workplaceId = shift?.facilityId;

  const isBadgeVerificationRedesignEnabled = useIsBadgeVerificationRedesignEnabled();
  const BadgeVerificationDialogComponent = isBadgeVerificationRedesignEnabled
    ? BadgeVerificationDialogRedesign
    : BadgeVerificationDialog;

  // Badge verification - check if verification is required for this shift and workplace
  const {
    isEnabled: isBadgeVerificationEnabled,
    isRequired: isBadgeVerificationRequired,
    isLoading: isBadgeVerificationLoading,
    error: badgeVerificationError,
  } = useIsBadgeVerificationRequired({
    shiftId,
    workplaceId,
  });

  const {
    isUploadingTimesheet,
    isUploadError,
    fileUploaderHandlers,
    inputFileRef,
    browserOnFileChange,
  } = useTimesheetFileUpload({
    shiftId,
    onUploadStart: onTimesheetUploadStart,
    onUploadError: onTimesheetUploadError,
  });

  const { inside, refetchGeofence, error: geofenceError } = useGeofenceContext();

  const match = useRouteMatch();

  const initialShiftStateLocation = useRef<UserLocationState | undefined>(undefined);

  if (isDefined(shiftState) && !initialShiftStateLocation.current) {
    initialShiftStateLocation.current = shiftState.metadata.userLocationState;
  }

  const isTimesheetEditDisabled = shiftState?.metadata.isTimeSheetEditDisabled ?? false;

  const { useExpectedArrivalTime } = useArrivalTimeConfig();

  const enforceNfcTimesheetBoundsFlag = useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
    defaultValue: false,
  });

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift?.facility?.userId,
  });

  const shiftSlots = useGetShiftSlotsV2({
    shiftIds: isDefined(shift) ? [shift._id] : [],
  });

  const { data: friendsList } = useGetFriendsList();
  const friendsMap = useMemo(() => {
    return new Map(friendsList?.data.map((friend) => [friend.id, friend]));
  }, [friendsList?.data]);

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(userId);

  const isClipboardScoreFreezeEnabled = clipboardScoreProfile?.scoreFreeze?.enabled ?? false;
  const clipboardScoreFreezeReason = clipboardScoreProfile?.scoreFreeze?.enabled
    ? clipboardScoreProfile?.scoreFreeze?.reason
    : undefined;

  const allQueries = [
    { isLoading: isShiftDetailsLoading, error: shiftsDetailsError },
    // Badge verification loading - prevents clock-in button from appearing
    // before we know if badge verification is required
    { isLoading: isBadgeVerificationLoading, error: badgeVerificationError },
  ];

  const isPageDataLoading = allQueries.some((q) => q.isLoading);
  const isPageDataError = allQueries.some((q) => q.error);
  const isPageDataSuccess = allQueries.every((q) => !q.isLoading && !q.error);

  const shiftStartDate = isDefined(shift) ? parseISO(shift.start) : undefined;

  const { startCancelFlow, cancelShiftBottomSheetProps } = useCancelShiftFlow({
    shiftId,
    shiftStartDate,
    currentDate,
    clockInStartIso: shift?.clockInOut?.start,
    orientationBlockId: shift?.orientationBlockId,
  });

  const {
    attendanceVerificationMethods,
    isMethodEnabled,
    getMethodConfig,
    externalTimeclockCredentials,
  } = useAttendanceVerificationMethod({
    shiftStateMetadata: shiftState?.metadata,
  });
  const nfcMethodConfig = getMethodConfig(AttendanceVerificationMethod.NFC_TAG);
  const nfcTagRequests =
    isDefined(nfcMethodConfig) && "tags" in nfcMethodConfig
      ? (nfcMethodConfig.tags ?? []).map((tag) => ({
          tagStatus: tag.status as NfcTagRequestStatus,
          tagLocation: tag.location,
        }))
      : [];

  const enforceNfcTimesheetBounds =
    enforceNfcTimesheetBoundsFlag && isMethodEnabled(AttendanceVerificationMethod.NFC_TAG);

  const [attendanceVerificationMethod] = attendanceVerificationMethods;

  const timekeepingMethod =
    attendanceVerificationMethod?.type ?? AttendanceVerificationMethod.GEOFENCE;

  const timekeepingAction: TimekeepingAction = "clock-in";

  const queryClient = useQueryClient();

  const { startTimekeepingActionTracking, completeTimekeepingActionTracking } =
    useTimekeepingActionTimer({
      action: timekeepingAction,
      shiftId,
      workerId: userId,
      workplaceId: isDefined(workplaceId) ? workplaceId : undefined,
      timekeepingMethod,
    });

  const handleTimekeepingActionCompleted = useCallback(
    (props?: { nonInstantPayReason?: string }) => {
      completeTimekeepingActionTracking(props);
      invalidateClipboardScoreProfile(queryClient, userId);
    },
    [completeTimekeepingActionTracking, queryClient, userId]
  );

  const onTimekeepingActionFailed = useCallback(
    ({ error }: { error: string }) => {
      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION_FAILED, {
        action: timekeepingAction,
        shiftId,
        workerId: userId,
        workplaceId,
        timekeepingMethod,
        error,
      });
    },
    [shiftId, userId, workplaceId, timekeepingMethod]
  );

  const onClockIn = useOnClockIn({
    shift,
    shiftState,
    currentDate,
    clockInModalState,
    tooFarAwayModalState,
    tooEarlyModalState,
    badgeVerificationModalState,
    inside,
    isBadgeVerificationRequired,
    onClockInInitiated: startTimekeepingActionTracking,
    onWillOpenClockInModal: () => {
      if (isMethodEnabled(AttendanceVerificationMethod.MESH_INTEGRATION)) {
        logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_CLOCK_IN_STARTED, {
          shiftId,
          type: "clock-in",
        });
      }
    },
  });

  const handleBadgeVerificationComplete = useCallback(() => {
    badgeVerificationModalState.closeModal();
    // Continue with clock-in flow, skip badge verification gate since it was handled
    onClockIn({ skipBadgeVerification: true });
  }, [badgeVerificationModalState, onClockIn]);

  const onCancelShift = useCallback(() => {
    startCancelFlow();
  }, [startCancelFlow]);

  const onUpdateETA = useCallback(() => {
    history.replace(
      generatePath(
        `${match.url}${SHIFT_DETAILS_UPDATE_ETA_PATH}?source=${UPDATE_ETA_BOTTOM_SHEET_SOURCE.SHIFT_DETAILS}`
      )
    );
  }, [history, match.url]);

  const onTransferShift = useCallback(() => {
    logEvent(USER_EVENTS.SHIFT_TRANSFERER_BUTTON_CLICKED, { shiftId, userId });
    updateTransferShiftModalState.openModal();
  }, [updateTransferShiftModalState, shiftId, userId]);

  const onSentHome = useCallback(() => {
    history.push(getSentHomeRequestCreatePath(shiftId));
  }, [history, shiftId]);

  // check geofence location for missed punch request submission
  const checkGeofenceLocation = useCallback(async (): Promise<{
    status: "inside" | "outside" | "unknown";
  }> => {
    const startTime = Date.now();
    setIsCheckingGeofence(true);

    let status: "inside" | "outside" | "unknown" = "unknown";

    try {
      // Try to refetch geofence status for the most up-to-date result
      const result = await refetchGeofence();

      if (result.error) {
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
          shiftId,
          workerId: userId,
          error: result.error instanceof Error ? result.error.message : "Unknown error",
        });
        status = "unknown";
      } else {
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
          shiftId,
          workerId: userId,
          isInsideGeofence: result.inside,
        });
        status = result.inside ? "inside" : "outside";
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
        shiftId,
        workerId: userId,
        error: errorMessage,
      });
      status = "unknown";
    } finally {
      /**
       * We want to have a minimum loader duration so
       * HCP can see that the action was executed,
       * even when it lasted for a short time.
       */
      const timeElapsedInMs = Date.now() - startTime;
      if (timeElapsedInMs < MIN_GEOFENCE_CHECK_LOADER_DURATION_MS) {
        await new Promise((resolve) => {
          geofenceCheckTimerRef.current = setTimeout(
            resolve,
            MIN_GEOFENCE_CHECK_LOADER_DURATION_MS - timeElapsedInMs
          );
        });
      }
      setIsCheckingGeofence(false);
    }

    return { status };
  }, [refetchGeofence, shiftId, userId]);

  // Navigate to submit timesheet page with or without signature
  const navigateToSubmitTimesheet = useCallback(
    (skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId }),
        search: searchParams.toString(),
      });
    },
    [history, shiftId]
  );

  const onUploadTimesheet = useCallback(async () => {
    if (shiftState?.metadata.isMissedPunchRequestEnabled) {
      // if worker is inside geofence or there is an error in fetching the geofence status, navigate without signature
      if (inside || isDefined(geofenceError)) {
        navigateToSubmitTimesheet(false);
      } else {
        // if worker is outside geofence, show return for signature modal
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
          shiftId,
          workerId: userId,
        });
        returnForSignatureModalState.openModal();
      }
    } else {
      uploadTimesheetModalState.openModal();
    }
  }, [
    shiftState?.metadata.isMissedPunchRequestEnabled,
    inside,
    geofenceError,
    navigateToSubmitTimesheet,
    shiftId,
    userId,
    returnForSignatureModalState,
    uploadTimesheetModalState,
  ]);

  // Handle "I'm still at the workplace" - recheck geofence
  const handleRecheckLocation = useCallback(async () => {
    const result = await checkGeofenceLocation();

    // If now inside geofence or unable to determine - close modal and navigate
    if (result.status === "inside" || result.status === "unknown") {
      returnForSignatureModalState.closeModal();
      navigateToSubmitTimesheet(false);
    }
    // If still outside, keep the bottom sheet open (user can try again or choose other options)
  }, [checkGeofenceLocation, returnForSignatureModalState, navigateToSubmitTimesheet]);

  // Handle confirmation to continue without signature
  const handleConfirmWithoutSignature = useCallback(() => {
    navigateToSubmitTimesheet(true);
  }, [navigateToSubmitTimesheet]);

  const delayedReviewConfig = useCbhFlag(CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW],
  });
  const isDelayedWorkplaceReviewEnabled = delayedReviewConfig.enabled;
  const { setDelayedReview } = useDelayedReviewStorage();

  const onContinueAfterUpload = useCallback(() => {
    uploadingTimesheetModalState.closeModal();

    if (isDelayedWorkplaceReviewEnabled && isDefined(shift)) {
      setDelayedReview({
        shiftId,
        facilityId: shift.facilityId,
        facilityName: shift.facility.name,
        facilityType: shift.facility.type ?? "Unknown",
        shiftEndTime: new Date().toISOString(),
      });
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [
    uploadingTimesheetModalState,
    postShiftReviewModalState,
    isDelayedWorkplaceReviewEnabled,
    shift,
    setDelayedReview,
    shiftId,
    history,
  ]);

  const onTryAgainAfterError = useCallback(() => {
    uploadingTimesheetModalState.closeModal();
    void onUploadTimesheet();
  }, [uploadingTimesheetModalState, onUploadTimesheet]);

  const handleAcceptNonInstantPay = useCallback(async () => {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.NO_LOCATION,
      });

      handleTimekeepingActionCompleted({
        nonInstantPayReason: "NO_LOCATION",
      });

      // refetch the shift state to get the latest data, as some flag depends on the shift ip state
      await refetchShiftState();

      acceptNonInstantPayModalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);
    }
  }, [
    acceptNonInstantPayModalState,
    markNonInstant,
    refetchShiftState,
    shiftId,
    showErrorToast,
    handleTimekeepingActionCompleted,
  ]);

  // this useLayoutEffect implements support for a simple query param `?clockIn` (without a value)
  // if that query param is detected, we trigger the clock in logic, and then delete the query param
  // one place we will use this is Live Activities
  // useLayoutEffect runs synchronously before paint, avoiding a flash of the page before the dialog opens
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);

    if (
      !isPageDataLoading &&
      isDefined(shift) &&
      isDefined(shiftState) &&
      params.has(SHIFT_DETAILS_CLOCK_IN_QUERY_PARAM)
    ) {
      logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOCK_IN_NOW_TAPPED, {
        shiftId,
        sourceUI: "deep-link",
      });
      onClockIn();
      // delete query param '&clockIn'
      params.delete(SHIFT_DETAILS_CLOCK_IN_QUERY_PARAM);
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }

    if (!isPageDataLoading && isBadgeVerificationEnabled) {
      const badgeStep = params.get(SHIFT_DETAILS_BADGE_VERIFICATION_STEP_QUERY_PARAM);
      const validBadgeSteps = new Set<string>(Object.values(BadgeVerificationStep));
      if (isDefined(badgeStep) && validBadgeSteps.has(badgeStep)) {
        setBadgeVerificationInitialStep(badgeStep as BadgeVerificationStep);
        badgeVerificationModalState.openModal();
        params.delete(SHIFT_DETAILS_BADGE_VERIFICATION_STEP_QUERY_PARAM);
        history.replace({
          pathname: location.pathname,
          search: params.toString(),
        });
      }
    }
  }, [
    isPageDataLoading,
    shift,
    shiftState,
    shiftId,
    onClockIn,
    history,
    location,
    badgeVerificationModalState,
    isBadgeVerificationEnabled,
  ]);

  const isSecondShiftInDouble = isDefined(shift)
    ? Boolean(shift.isAfterAnotherShift) || Boolean(shift.isSecondShiftInDouble)
    : false;

  const minutesElapsed = isDefined(shiftStartDate)
    ? calculateMinutesElapsed(shiftStartDate, currentDate)
    : 0;

  const earliestGeoFenceEntryTime = isDefined(shift?.geofence?.entry)
    ? parseISO(shift.geofence.entry)
    : undefined;

  const targetArrivalTime = isDefined(shift?.computedArrival?.targetArrivalTime)
    ? parseISO(shift.computedArrival.targetArrivalTime)
    : undefined;

  const shiftBookedAt = isDefined(shift?.bookedAt) ? parseISO(shift.bookedAt) : undefined;
  const { badgeNumber } = externalTimeclockCredentials;

  function getClockInBottomSheet() {
    if (
      isPageDataLoading ||
      !isDefined(shift) ||
      !isDefined(shiftState) ||
      !isDefined(shiftStartDate)
    ) {
      return null;
    }

    if (
      isDefined(badgeNumber) &&
      (attendanceVerificationMethod?.type === AttendanceVerificationMethod.UKG_INTEGRATION ||
        attendanceVerificationMethod?.type ===
          AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION)
    ) {
      return (
        <ClockInTimekeepingIntegrationBottomSheet
          modalState={clockInModalState}
          workerId={shift.agentId}
          workplaceId={shift.facilityId}
          workplaceName={shift.facility.name}
          config={attendanceVerificationMethod}
          workerCredentials={{ badgeNumber }}
          shiftId={shift._id}
          shiftStartDate={shiftStartDate}
          earliestGeofenceEntryTime={earliestGeoFenceEntryTime}
          requiresBreakPolicyAcknowledgement={requiresBreakPolicyAcknowledgement}
          breakPolicyNoteId={breakPolicyNoteId ?? ""}
          workplaceRules={workplaceRules}
          shouldShowWorkplaceRulesReminder={shouldShowWorkplaceRulesReminder}
          isSecondShiftInDouble={
            Boolean(shift.isAfterAnotherShift) || Boolean(shift.isSecondShiftInDouble)
          }
          targetArrivalTime={targetArrivalTime}
          timezone={shift.facility.tmz ?? undefined}
          shiftBookedAt={shiftBookedAt}
          isTimesheetEditDisabled={isTimesheetEditDisabled}
          shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
          isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
          workerStage={workerStage}
          isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
          clipboardScoreFreezeReason={clipboardScoreFreezeReason}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.MESH_INTEGRATION) {
      return (
        <ClockInTouchpointBottomSheet
          modalState={clockInModalState}
          shift={shift}
          shiftStartDate={shiftStartDate}
          workerId={shift.agentId}
          workplaceId={shift.facilityId}
          workerStage={workerStage}
          workerSubstage={workerSubstage?.name}
          workplaceName={shift.facility.name}
          kioskLocation={attendanceVerificationMethod.kioskLocation ?? ""}
          timeToAllowSkipInSec={
            attendanceVerificationMethod.timeToAllowSkipInSec ??
            DEFAULT_MESH_TIME_TO_ALLOW_SKIP_IN_SEC
          }
          nfcTagRequests={nfcTagRequests}
          isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
          requiresBreakPolicyAcknowledgement={requiresBreakPolicyAcknowledgement}
          isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled ?? false}
          breakPolicyNoteId={breakPolicyNoteId ?? ""}
          workplaceRules={workplaceRules}
          shouldShowWorkplaceRulesReminder={shouldShowWorkplaceRulesReminder}
          isSecondShiftInDouble={isSecondShiftInDouble}
          targetArrivalTime={targetArrivalTime}
          shiftBookedAt={shiftBookedAt}
          isTimesheetEditDisabled={isTimesheetEditDisabled}
          isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
          clipboardScoreFreezeReason={clipboardScoreFreezeReason}
          shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
          isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
          timezone={shift.facility.tmz ?? undefined}
          qrCodeProps={{
            shiftId: shift._id,
            agentId: shift.agentId,
            agentFirstName: firstName ?? "",
            agentLastName: lastName ?? "",
            agentType: shift.agentReq,
            facilityId: shift.facilityId,
            facilityName: shift.facility.name,
            agencyId: attendanceVerificationMethod.asAgencyId ?? DEFAULT_MESH_AGENCY_ID,
            agencyName: attendanceVerificationMethod.asAgencyName ?? DEFAULT_MESH_AGENCY_NAME,
            punchType: "clock-in",
          }}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.TAKE_A_VIDEO) {
      return (
        <ClockInTimeclockComplianceBottomSheet
          modalState={clockInModalState}
          shift={shift}
          attendanceVerificationMethod={attendanceVerificationMethod}
          workerCredentials={{ pin: externalTimeclockCredentials.pin ?? "" }}
          earliestGeofenceEntryTime={earliestGeoFenceEntryTime}
          workerId={shift.agentId}
          workerStage={workerStage}
          workerSubstage={workerSubstage?.name}
          workplaceId={shift.facilityId}
          workplaceName={shift.facility.name}
          requiresBreakPolicyAcknowledgement={requiresBreakPolicyAcknowledgement}
          breakPolicyNoteId={breakPolicyNoteId ?? ""}
          workplaceRules={workplaceRules}
          shouldShowWorkplaceRulesReminder={shouldShowWorkplaceRulesReminder}
          isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
          isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled ?? false}
          isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
          nfcTagRequests={nfcTagRequests}
          shiftState={shiftState}
          isSecondShiftInDouble={isSecondShiftInDouble}
          targetArrivalTime={targetArrivalTime}
          shiftStartDate={shiftStartDate}
          shiftBookedAt={shiftBookedAt}
          isTimesheetEditDisabled={isTimesheetEditDisabled}
          shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
          isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
          timezone={shift.facility.tmz ?? undefined}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.AI_TAKE_A_VIDEO) {
      return (
        <ClockInWithVideoValidationBottomSheet
          modalState={clockInModalState}
          shift={shift}
          attendanceVerificationMethod={attendanceVerificationMethod}
          workerCredentials={{ pin: externalTimeclockCredentials.pin ?? "" }}
          earliestGeofenceEntryTime={earliestGeoFenceEntryTime}
          workerId={shift.agentId}
          workerStage={workerStage}
          workplaceId={shift.facilityId}
          workplaceName={shift.facility.name}
          requiresBreakPolicyAcknowledgement={requiresBreakPolicyAcknowledgement}
          breakPolicyNoteId={breakPolicyNoteId ?? ""}
          workplaceRules={workplaceRules}
          shouldShowWorkplaceRulesReminder={shouldShowWorkplaceRulesReminder}
          isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
          isSecondShiftInDouble={isSecondShiftInDouble}
          targetArrivalTime={targetArrivalTime}
          shiftStartDate={shiftStartDate}
          shiftBookedAt={shiftBookedAt}
          isTimesheetEditDisabled={isTimesheetEditDisabled}
          shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
          isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
          timezone={shift.facility.tmz ?? undefined}
          isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled ?? false}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    return (
      <ClockInBottomSheet
        isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled ?? false}
        breakPolicyNoteId={breakPolicyNoteId ?? ""}
        modalState={clockInModalState}
        shiftId={shift._id}
        shiftStartDate={shiftStartDate}
        earliestGeofenceEntryTime={earliestGeoFenceEntryTime}
        workerId={shift.agentId}
        workplaceId={shift.facilityId}
        workplaceName={shift.facility.name}
        isNfcClockInEnabled={
          attendanceVerificationMethod?.type === AttendanceVerificationMethod.NFC_TAG
        }
        nfcTagRequests={nfcTagRequests}
        requiresBreakPolicyAcknowledgement={requiresBreakPolicyAcknowledgement}
        workplaceRules={workplaceRules}
        shouldShowWorkplaceRulesReminder={shouldShowWorkplaceRulesReminder}
        isSecondShiftInDouble={isSecondShiftInDouble}
        targetArrivalTime={targetArrivalTime}
        shiftBookedAt={shiftBookedAt}
        isTimesheetEditDisabled={isTimesheetEditDisabled}
        shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
        isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
        timezone={shift.facility.tmz ?? undefined}
        workerStage={workerStage}
        workerSubstage={workerSubstage?.name}
        isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
        clipboardScoreFreezeReason={clipboardScoreFreezeReason}
        onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
        onTimekeepingActionFailed={onTimekeepingActionFailed}
      />
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        {isDefined(shift) && isDefined(shiftState) && isDefined(shiftStartDate) ? (
          <ShiftDetailsHeaderWrapper
            shift={shift}
            shiftState={shiftState}
            isTestAccount={isWorkerTestAccount ?? false}
            shiftAgentReq={shift.agentReq}
            shiftStartDate={shiftStartDate}
            isPageDataSuccess={isPageDataSuccess}
            workplaceTimeZone={shift.facility.tmz}
            shiftOptionsModalState={shiftOptionsModalState}
            isBadgeVerificationEnabled={isBadgeVerificationEnabled}
            onOpenShiftOptions={handleOpenShiftOptions}
            onCloseShiftOptions={handleCloseShiftOptions}
            onClockIn={onClockIn}
            onUpdateETA={onUpdateETA}
            onCancelShift={onCancelShift}
            onTransferShift={onTransferShift}
            onUploadTimesheet={() => {
              void onUploadTimesheet();
            }}
            onAcceptNonInstantPay={() => {
              acceptNonInstantPayModalState.openModal();
            }}
          />
        ) : null}

        <PageContent>
          {isPageDataLoading ? <ShiftDetailsPageSkeleton /> : null}

          {isPageDataError ? (
            <Alert sx={{ marginY: 4 }} severity="error">
              Something went wrong while loading this shift. Please try again.
            </Alert>
          ) : null}

          {!isPageDataLoading && isDefined(shift) && isDefined(shiftState) ? (
            <List>
              <ShiftDurationListItem
                shiftTimeSlot={shiftPeriodToTimeSlot[shift.name as ShiftPeriod]}
                shiftStartIso={shift.start}
                shiftEndIso={parseISO(shift.end).toISOString()}
                shiftDurationInHours={
                  differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) / 60
                }
                breakDurationInMinutes={mandatoryBreakDurationInMinutes}
                requiresBreak={shift.facility.requiresLunchBreak ?? false}
                workplaceTimeZone={shift.facility.tmz}
                minutesElapsed={minutesElapsed}
              />
              <HorizontalCardWrapperListItem
                shift={shift}
                workerId={userId}
                isMandatoryBreakEnabled={shift.facility.requiresLunchBreak ?? false}
                shiftState={shiftState}
                isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              />
              {shift.shiftSpecifications?.map((specification) => (
                <ShiftBottomSheetSpecification
                  key={`${specification.category}-${specification.requirement}`}
                  category={specification.category}
                  requirement={specification.requirement}
                />
              ))}
              <ShiftWorkplaceListItem
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceType={shift.facility.type ?? ""}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_WORKPLACE_CARD_TAPPED, {
                    shiftId: shift._id,
                    workplaceId: shift.facilityId,
                    sourceUI: "shift-details-page",
                  });

                  history.push({
                    pathname: generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
                      workplaceId: shift.facilityId,
                    }),
                    state: {
                      clipboardGuidesShiftContext: {
                        shiftId: shift._id,
                        shiftEnd: shift.end,
                        isVerified: shift.verified === true,
                        isDeleted: shift.deleted === true,
                        isClockedOut: isDefined(shift.clockInOut?.end),
                        qualification: shift.agentReq,
                      },
                    },
                  });
                }}
              />
              <ShiftHighlightsAndMapListItem
                isMapEnabled
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                workplaceFormattedAddress={shift.facility.fullAddress?.formatted}
                isDividerEnabled={false}
              />
              {(() => {
                const slotId = convertShiftToSlotId({
                  start: shift.start,
                  end: shift.end,
                  facilityId: shift.facility.userId ?? shift.facilityId,
                });
                const rawSlot = shiftSlots[slotId];

                const filteredAssignments =
                  rawSlot?.shiftAssignments?.filter((a) => a.workerId !== userId) ?? [];

                const hasOthers = filteredAssignments.length > 0;

                if (!hasOthers) {
                  return null;
                }

                const filteredSlot = rawSlot
                  ? { ...rawSlot, shiftAssignments: filteredAssignments }
                  : undefined;

                return (
                  <WorkWithFriendsProvider>
                    <WorkingThisShiftListItem
                      shiftSlots={filteredSlot}
                      friendsMap={friendsMap}
                      isBookable={false}
                    />
                  </WorkWithFriendsProvider>
                );
              })()}
            </List>
          ) : null}
        </PageContent>

        {!isPageDataLoading && isDefined(shift) && isDefined(shiftState) && (
          <ShiftDetailsFooter
            shift={shift}
            shiftState={shiftState}
            currentDate={currentDate}
            isUploadingTimesheet={isUploadingTimesheet}
            isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
            isBadgeVerificationEnabled={isBadgeVerificationEnabled}
            onClockIn={onClockIn}
            onUpdateETA={onUpdateETA}
            onCancelShift={onCancelShift}
            onTransferShift={onTransferShift}
            onSentHome={onSentHome}
            onUploadTimesheet={() => {
              void onUploadTimesheet();
            }}
          />
        )}
      </PageWrapper>

      {isDefined(cancelShiftBottomSheetProps) && (
        <CancelShiftBottomSheet {...cancelShiftBottomSheetProps} />
      )}

      {getClockInBottomSheet()}

      {!isPageDataLoading && isDefined(shift) ? (
        <TransferShiftBottomSheet modalState={updateTransferShiftModalState} shiftId={shift._id} />
      ) : null}

      <>
        <input
          ref={inputFileRef}
          style={{ display: "none" }}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/jpg"
          onChange={browserOnFileChange}
        />
        <FileUploaderBottomSheet
          modalState={uploadTimesheetModalState}
          onTakePhoto={fileUploaderHandlers.onTakePhoto}
          onSelectPhotoFromCameraRoll={fileUploaderHandlers.onSelectPhotoFromCameraRoll}
          onSelectFile={fileUploaderHandlers.onSelectFile}
        />
      </>

      {!isPageDataLoading && isDefined(shift) && isBadgeVerificationEnabled && (
        <BadgeVerificationDialogComponent
          shiftId={shiftId}
          qualificationName={shift.agentReq}
          modalState={badgeVerificationModalState}
          initialStep={badgeVerificationInitialStep}
          onSubmitPhoto={handleBadgeVerificationComplete}
          onSkipBadgeVerification={handleBadgeVerificationComplete}
        />
      )}

      {isDefined(shift) && isDefined(shiftState) && (
        <TooEarlyToClockInBottomSheet
          modalState={tooEarlyModalState}
          shiftStart={shift.start}
          workplaceTimezone={shift.facility.tmz}
          earlyClockInEnabledInMinutes={
            shiftState.metadata.isEarlyClockInEnabled
              ? shiftState.metadata.earlyClockInEnabledInMinutes
              : undefined
          }
        />
      )}

      {!isPageDataLoading && isDefined(shift) ? (
        <>
          <TooFarAwayBottomSheet
            modalState={tooFarAwayModalState}
            workplaceName={shift.facility.name}
            onTryAgain={async () => {
              tooFarAwayModalState.closeModal();
              // If still not inside after retry, user can try again or skip location check
              onClockIn();
            }}
            onSkipLocationCheck={() => {
              tooFarAwayModalState.closeModal();
              acceptNonInstantPayModalState.openModal();
            }}
            onCancel={() => {
              tooFarAwayModalState.closeModal();
            }}
          />
          <AcceptNonInstantPayBottomSheet
            modalState={acceptNonInstantPayModalState}
            isConvertingToNonInstantPay={isConvertingToNonInstantPay}
            isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled ?? false}
            onAccept={handleAcceptNonInstantPay}
            onCancel={() => {
              acceptNonInstantPayModalState.closeModal();
              tooFarAwayModalState.openModal();
            }}
          />
        </>
      ) : null}

      <TimesheetUploadBottomSheet
        modalState={uploadingTimesheetModalState}
        isUploading={isUploadingTimesheet}
        isUploadError={isUploadError}
        onContinue={onContinueAfterUpload}
        onTryAgain={onTryAgainAfterError}
      />

      {isDefined(shift) && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: shift.facilityId,
            name: shift.facility.name,
            type: shift.facility.type ?? "Unknown",
            // Note: facility photo not available in shift data
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={() => {
            postShiftReviewModalState.closeModal();
            history.replace(MY_SHIFTS_FULL_PATH);
          }}
        />
      )}

      {/* Return for Signature Bottom Sheet for Missed punch request */}
      <ReturnForSignatureBottomSheet
        modalState={returnForSignatureModalState}
        shiftId={shiftId}
        workerId={userId}
        isCheckingLocation={isCheckingGeofence}
        isShiftVerified={shift?.verified ?? false}
        onRecheckLocation={handleRecheckLocation}
        onCancel={() => {
          returnForSignatureModalState.closeModal();
        }}
        onConfirm={handleConfirmWithoutSignature}
      />

      <ModalTransitionSwitch>
        <ModalRoute
          shouldReplacePathOnBack
          closeModalPath={match.url}
          path={`${match.url}${SHIFT_DETAILS_CONFIRM_ATTENDANCE_PATH}`}
          render={({ modalState }) => (
            <ConfirmAttendanceBottomSheet
              modalState={modalState}
              shiftId={shiftId}
              onCancelShift={() => {
                startCancelFlow();
              }}
            />
          )}
        />
      </ModalTransitionSwitch>
    </ThemeProvider>
  );
}

/**
 * "Don't do this at home"; the use of a search param to track modal open/closed is NOT
 * a good idea. We're tracking a future improvement here via
 * https://linear.app/clipboardhealth/issue/TX-2514/support-homemyshiftsshiftidshiftoptions-route-for-modal-based-ui
 * to implement a proper solution. The TL;DR is that we want to take a better approach by defining a proper
 * "modal is open" path to track the modal open/closed state.
 *
 */
function useShiftOptionsModal() {
  const history = useHistory();
  const location = useLocation();
  const modalState = useModalState();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("shiftOptionsOpen") === "1") {
      modalState.openModal();
    } else {
      modalState.closeModal();
    }
  }, [location.search, modalState]);

  const handleOpenShiftOptions = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("shiftOptionsOpen", "1");
    history.replace({ ...location, search: params.toString() });
  }, [history, location]);

  const handleCloseShiftOptions = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("shiftOptionsOpen");
    history.replace({ ...location, search: params.toString() });
  }, [history, location]);

  return {
    modalState,
    handleOpenShiftOptions,
    handleCloseShiftOptions,
  };
}

export function ShiftDetailsPage() {
  const { shiftId } = useParams<{ shiftId: string }>();
  const { userId } = useDefinedWorker();
  const initialShiftStateLocation = useRef<UserLocationState | undefined>(undefined);

  const { shift, shiftState } = useGetShiftDetailsData({
    shiftId,
  });

  if (isDefined(shiftState) && !initialShiftStateLocation.current) {
    initialShiftStateLocation.current = shiftState.metadata.userLocationState;
  }

  const facilityGeoLocation = isDefined(shift?.facility.geoLocation.coordinates)
    ? convertToGeoLocation(shift?.facility.geoLocation.coordinates)
    : undefined;

  const enableGeofenceTracking = useEnableGeofenceTracking({
    shiftStart: shift?.start,
    shiftState,
  });

  return (
    <GeofenceProvider
      shiftId={shiftId}
      workerId={userId}
      workplaceId={shift?.facilityId}
      initialLocation={initialShiftStateLocation.current}
      fallbackGeofenceLocation={facilityGeoLocation}
      enableGeofenceTracking={enableGeofenceTracking}
    >
      <OpenGeoLocationProvider
        location={
          isDefined(shift?.facility.geoLocation.coordinates)
            ? {
                lng: shift?.facility.geoLocation.coordinates[0],
                lat: shift?.facility.geoLocation.coordinates[1],
              }
            : undefined
        }
        name={shift?.facility.name}
        formattedAddress={shift?.facility.fullAddress?.formatted}
      >
        <ShiftDetailsPageContent />
        <ShiftDetailsModalRoutes shiftId={shiftId} />
      </OpenGeoLocationProvider>
    </GeofenceProvider>
  );
}
/* eslint-enable max-lines */
